import { RefreshCw, Users } from 'lucide-react';
import type { AdminInvitationRecord } from '../../services/api';
import { formatBeijingDateTime } from '../../utils/time';

interface InvitationsTabProps {
  invitations: AdminInvitationRecord[];
  loading: boolean;
  onRefresh: () => void;
}

function formatDisplayName(name: string | null, email: string): string {
  return name?.trim() || email;
}

export default function InvitationsTab({
  invitations,
  loading,
  onRefresh,
}: InvitationsTabProps) {
  const totalInvitations = invitations.length;
  const totalInviterBonus = invitations.reduce((sum, invitation) => sum + invitation.inviterBonus, 0);
  const totalInviteeBonus = invitations.reduce((sum, invitation) => sum + invitation.inviteeBonus, 0);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">邀请记录</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">{totalInvitations}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">成功邀请人数</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">{totalInvitations}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">邀请人发放积分</p>
          <p className="text-xl lg:text-2xl font-bold text-green-600 mt-2">+{totalInviterBonus}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">被邀请人发放积分</p>
          <p className="text-xl lg:text-2xl font-bold text-blue-600 mt-2">+{totalInviteeBonus}</p>
        </div>
      </div>

      <div className="bg-white border border-light-border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-light-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-light-text">邀请记录列表</h3>
            <p className="text-sm text-light-muted mt-1">查看邀请人、被邀请人以及双方奖励积分</p>
          </div>
          <button
            onClick={onRefresh}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            刷新
          </button>
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-light-hover text-left text-sm text-light-muted">
                <th className="px-5 py-3 font-medium">邀请人</th>
                <th className="px-5 py-3 font-medium">邀请人邮箱</th>
                <th className="px-5 py-3 font-medium">被邀请人</th>
                <th className="px-5 py-3 font-medium">被邀请人邮箱</th>
                <th className="px-5 py-3 font-medium">邀请人奖励</th>
                <th className="px-5 py-3 font-medium">被邀请人奖励</th>
                <th className="px-5 py-3 font-medium">邀请时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-border">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-light-muted">加载中...</td>
                </tr>
              ) : invitations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-light-muted">暂无邀请记录</td>
                </tr>
              ) : (
                invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-light-hover/50 transition-colors">
                    <td className="px-5 py-4 text-sm font-medium text-light-text">
                      {formatDisplayName(invitation.inviterName, invitation.inviterEmail)}
                    </td>
                    <td className="px-5 py-4 text-sm text-light-muted">{invitation.inviterEmail}</td>
                    <td className="px-5 py-4 text-sm font-medium text-light-text">
                      {formatDisplayName(invitation.inviteeName, invitation.inviteeEmail)}
                    </td>
                    <td className="px-5 py-4 text-sm text-light-muted">{invitation.inviteeEmail}</td>
                    <td className="px-5 py-4 text-sm font-medium text-green-600">+{invitation.inviterBonus}</td>
                    <td className="px-5 py-4 text-sm font-medium text-blue-600">+{invitation.inviteeBonus}</td>
                    <td className="px-5 py-4 text-sm text-light-muted whitespace-nowrap">
                      {formatBeijingDateTime(invitation.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="px-5 py-10 text-center text-light-muted">加载中...</div>
          ) : invitations.length === 0 ? (
            <div className="px-5 py-10 text-center text-light-muted">暂无邀请记录</div>
          ) : (
            invitations.map((invitation) => (
              <div key={invitation.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-light-text">
                      {formatDisplayName(invitation.inviterName, invitation.inviterEmail)}
                    </p>
                    <p className="text-xs text-light-muted mt-1">邀请人</p>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-green-50 text-green-600 rounded-lg text-xs font-medium">
                    <Users className="w-3.5 h-3.5" />
                    +{invitation.inviterBonus}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">邀请人邮箱</p>
                    <p className="text-xs text-light-text mt-1 break-all">{invitation.inviterEmail}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">被邀请人邮箱</p>
                    <p className="text-xs text-light-text mt-1 break-all">{invitation.inviteeEmail}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">被邀请人</p>
                    <p className="text-xs text-light-text mt-1">
                      {formatDisplayName(invitation.inviteeName, invitation.inviteeEmail)}
                    </p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">被邀请人奖励</p>
                    <p className="text-xs font-medium text-blue-600 mt-1">+{invitation.inviteeBonus}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-light-muted">邀请时间</span>
                  <span className="text-light-text">{formatBeijingDateTime(invitation.createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
